<template>
  <div class="location">
    <span>当前位置：</span>
    <span><a :href="homeUrl">首页</a></span>
    <span v-for="(item, index) in curMapColumnList" :key="index" class="item">
      <a :href="item.url">{{ item.title }}</a>
    </span>
  </div>
</template>

<script setup>
// #region 引用
import useSSRInitialStateStore from '@/stores/ssr-state';
import { useSSRContext } from '@/ssr/use-context';
// #endregion

// #region props/emit
const props = defineProps({
  contentNavList: {
    type: Array,
    default: () => [],
  },
});
// #endregion

// #region 变量/常量

const ssrInitialState = useSSRInitialStateStore();
const curMapColumnList = ref([]); // 当前位置的栏目列表
const homeUrl = ref();

// #endregion

// #region 业务方法

// SSR 期间同步读取数据（父组件 onServerPrefetch 已设置）
if (import.meta.env.SSR) {
  const ssrContext = useSSRContext();
  curMapColumnList.value =
    ssrContext.initialState?.data?.current?.curMapColumnList || [];

  if (props.contentNavList.length > 0) {
    curMapColumnList.value.push(...props.contentNavList);
  }
  homeUrl.value = ssrContext.initialState?.data?.current?.homeUrl;
} else {
  // 客户端从initialState读取数据
  curMapColumnList.value = ssrInitialState.data.current.curMapColumnList;
  homeUrl.value = ssrInitialState.data.current.homeUrl;
}

// #endregion
</script>

<style scoped lang="scss">
@import '@/styles/website/_variables.scss';

// ============================================
// 当前位置组件变量（继承官网设计系统）
// ============================================
$location-color-text: $website-text-secondary;     // 文字颜色
$location-color-link: $website-text-primary;       // 链接颜色
$location-color-link-hover: $website-primary;      // hover颜色

// 动画时长
$location-duration-normal: $website-duration-normal;

// 动画曲线
$location-ease-out: $website-ease-out;

/* PC端样式 */
@include pc {
  .location {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 1400px;
    height: 50px;
    padding: 0 0 0 10px;
    margin: 0 auto;
    margin-bottom: 15px;
    font-size: 16px;
    line-height: 50px;
    color: $location-color-text;
  }

  .location .item,
  .location span {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
  }

  .location span a {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-left: 5px;
    color: $location-color-link;
    text-decoration: none;
    transition: color $location-duration-normal $location-ease-out;

    &:hover {
      color: $location-color-link-hover;
    }

    // 下划线动画（从左到右展开）
    &::after {
      content: '';
      position: absolute;
      bottom: 8px;
      left: 0;
      width: 0;
      height: 2px;
      background: linear-gradient(90deg,
        $location-color-link-hover,
        $website-primary-light
      );
      transition: width $location-duration-normal $location-ease-out;
    }

    &:hover::after {
      width: 100%;
    }
  }

  // 首页图标（使用mask技术显示渐变色）
  .location span:first-of-type::before {
    display: inline-block;
    width: 14px;
    height: 14px;
    margin-right: 6px;
    content: '';
    background: linear-gradient(135deg, $website-primary, $website-primary-light);
    -webkit-mask-image: url('./image/home.svg');
    mask-image: url('./image/home.svg');
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    transition: transform $location-duration-normal $location-ease-out;
  }

  // hover时首页图标轻微放大
  .location span:first-of-type:hover::before {
    transform: scale(1.1);
  }

  // 分隔符改为渐变圆点
  .location span:not(:first-of-type, :last-of-type)::after {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 8px;
    margin-right: 8px;
    content: '';
    background: linear-gradient(135deg,
      $website-primary,
      $website-primary-light
    );
    border-radius: 50%;
    transition: transform $location-duration-normal $location-ease-out;
  }

  // hover时圆点放大
  .location span:not(:first-of-type, :last-of-type):hover::after {
    transform: scale(1.5);
  }
}
</style>

<style scoped lang="scss">
@import '@/styles/website/_variables.scss';

/* 移动端样式 */
@include mobile {
  // 移动端变量（继承官网设计系统）
  $location-color-text: $website-text-secondary;
  $location-color-link: $website-text-primary;
  $location-color-link-hover: $website-primary;
  $location-duration-normal: $website-duration-normal;
  $location-ease-out: $website-ease-out;

  .location {
    display: flex;
    align-items: center;
    width: 100%;
    height: 12.5vw;
    padding: 2vw 3vw;
    margin: 0 auto;
    margin-bottom: 1vw;
    font-size: 4vw;
    line-height: 2;
    color: $location-color-text;
  }

  .location .item,
  .location span {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .location span a {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    color: $location-color-link;
    text-decoration: none;
    transition: color $location-duration-normal $location-ease-out;

    &:active {
      color: $location-color-link-hover;
    }
  }

  // 位置图标（使用mask技术显示渐变色）- [灵动-2026-03-10] 微跳动
  .location span:first-of-type::before {
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 1vw;
    content: '';
    background: linear-gradient(135deg, $website-primary, $website-primary-light);
    -webkit-mask-image: url('./image/location.svg');
    mask-image: url('./image/location.svg');
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    animation: iconBounce 2s ease-in-out infinite;
  }

  // 分隔符改为圆点（移动端适配）- [灵动-2026-03-10] 脉动效果
  .location span:not(:first-of-type, :last-of-type)::after {
    display: inline-block;
    width: 2vw;
    height: 2vw;
    margin: 0 1.5vw;
    content: '';
    background: linear-gradient(135deg,
      $website-primary,
      $website-primary-light
    );
    border-radius: 50%;
    animation: dotPulse 1.5s ease-in-out infinite;
  }
  
  // 错落动画延迟
  .location span:nth-child(2)::after { animation-delay: 0.2s; }
  .location span:nth-child(3)::after { animation-delay: 0.4s; }
  .location span:nth-child(4)::after { animation-delay: 0.6s; }
}
</style>
